// lib/provider-errors.ts
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeMessage = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const parseJsonString = (value: string): unknown | null => {
  const trimmed = value.trim();
  if (!trimmed || (!trimmed.startsWith('{') && !trimmed.startsWith('['))) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const extractDetailMessages = (payload: unknown): string | null => {
  if (!isRecord(payload)) return null;
  const detail = payload.detail;

  if (typeof detail === 'string') {
    return normalizeMessage(detail);
  }

  if (!Array.isArray(detail)) return null;

  const messages = detail
    .map((entry) => {
      if (!isRecord(entry)) return null;
      return normalizeMessage(entry.msg) ?? normalizeMessage(entry.message);
    })
    .filter((message): message is string => Boolean(message));

  return messages.length > 0 ? messages.join(' ') : null;
};

const candidatePayloadsFromError = (error: unknown): unknown[] => {
  const payloads: unknown[] = [error];
  if (isRecord(error)) {
    payloads.push(error.body);
    payloads.push(error.response);
    if (isRecord(error.response)) payloads.push(error.response.data);
    payloads.push(error.data);
    payloads.push(error.error);
    payloads.push(error.message);
  }
  if (error instanceof Error) {
    payloads.push(error.message);
  }
  return payloads;
};

export const extractStructuredProviderErrorMessage = (error: unknown): string | null => {
  for (const payload of candidatePayloadsFromError(error)) {
    const detailMessage = extractDetailMessages(payload);
    if (detailMessage) return detailMessage;

    if (typeof payload === 'string') {
      const parsed = parseJsonString(payload);
      const parsedDetailMessage = extractDetailMessages(parsed);
      if (parsedDetailMessage) return parsedDetailMessage;
    }
  }

  return null;
};

const extractProviderErrorMessage = (
  error: unknown,
  fallback = 'Provider request failed.'
): string => {
  const structuredMessage = extractStructuredProviderErrorMessage(error);
  if (structuredMessage) return structuredMessage;

  for (const payload of candidatePayloadsFromError(error)) {
    if (isRecord(payload)) {
      const message = normalizeMessage(payload.message);
      if (message) return message;
      const nestedError = payload.error;
      if (isRecord(nestedError)) {
        const nestedMessage = normalizeMessage(nestedError.message);
        if (nestedMessage) return nestedMessage;
      }
      const errorText = normalizeMessage(nestedError);
      if (errorText) {
        const parsed = parseJsonString(errorText);
        const parsedMessage = extractStructuredProviderErrorMessage(parsed);
        if (parsedMessage) return parsedMessage;
        return errorText;
      }
    }

    const message = normalizeMessage(payload);
    if (message) return message;
  }

  return fallback;
};

export const extractFalErrorMessage = (error: unknown): string =>
  extractProviderErrorMessage(error, 'fal.ai request failed.');

export const extractSumeErrorMessage = (payload: unknown): string =>
  extractProviderErrorMessage(payload, 'no reason given.');
